/**
 * The Level Up assessment package — the ONE portable shape of an assessment's
 * content (`releve.level-up/v1`): what the Phase 2 import / export and the
 * builder exchange, and what `convertLegacyGsaImport` produces
 * (docs/LEVEL_UP.md "Names" / "Data model").
 *
 *   assessment  { key, name }                          → level_up_assessments
 *   metadata    { tierNames, badgeBands, helpLinks }   → level_up_versions.metadata
 *   pillars[]   { key, name, color?, icon? }           → level_up_pillars
 *   metrics[]   { key, pillarKey, number, …, tiers[] } → level_up_metrics / level_up_tiers
 *
 * Array order is `sort_order`. There is no `points` field: a tier's points are
 * GENERATED in the database as `difficulty + impact`.
 *
 * Text is plain text, scrubbed by the survey scrubber (`surveyText`), and
 * localized as `{ en, nl?, fr? }` — `en` required. Length caps live here only
 * (the SQL CHECKs test shape, not length).
 *
 * The contiguous-tiers rule (tiers run 0..N with no gap) has NO SQL backstop:
 * the Phase 2 publish RPC must re-check it.
 */
import { z } from "zod";
import { surveyText } from "../schemas/surveys.js";
// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------
export const LEVEL_UP_LOCALES = ["en", "nl", "fr"];
export const LEVEL_UP_PACKAGE_FORMAT = "releve.level-up/v1";
/** Length bounds of a key (the one source for the pattern, the slugger and the key builder). */
export const LEVEL_UP_KEY_MIN = 2;
export const LEVEL_UP_KEY_MAX = 64;
/** Keys and slugs of assessments, pillars and metrics (the SQL CHECK is the same pattern). */
export const LEVEL_UP_KEY_PATTERN = new RegExp(`^[a-z0-9-]{${LEVEL_UP_KEY_MIN},${LEVEL_UP_KEY_MAX}}$`);
export const LEVEL_UP_NAME_MAX = 200;
export const LEVEL_UP_SUBCATEGORY_MAX = 200;
export const LEVEL_UP_FOCUS_MAX = 200;
export const LEVEL_UP_DESCRIPTION_MAX = 5_000;
export const LEVEL_UP_TIER_NAME_MAX = 100;
export const LEVEL_UP_TIER_LABEL_MAX = 100;
export const LEVEL_UP_STATEMENT_MAX = 2_000;
export const LEVEL_UP_TERMS_BODY_MAX = 50_000;
export const LEVEL_UP_LINK_TITLE_MAX = 200;
export const LEVEL_UP_URL_MAX = 2_048;
export const LEVEL_UP_FRAMEWORK_VALUE_MAX = 100;
export const LEVEL_UP_MAX_PILLARS = 20;
export const LEVEL_UP_MAX_METRICS = 500;
export const LEVEL_UP_MAX_LINKS = 20;
export const LEVEL_UP_MAX_HELP_LINKS = 20;
export const LEVEL_UP_MAX_FRAMEWORK_VALUES = 20;
/** Tiers are 0..9 (the SQL CHECK on `tier_level`). */
export const LEVEL_UP_MAX_TIER_LEVEL = 9;
/** Difficulty, impact and cost are whole numbers 0..5 (Phase 2 ruling; mirrored by the §0b CHECKs of 20261013140000). */
export const LEVEL_UP_MAX_RATING = 5;
/** Metric number cap. */
export const LEVEL_UP_MAX_METRIC_NUMBER = 9_999;
export const LEVEL_UP_BADGE_BAND_COUNT = 5;
/** `{ en, nl?, fr? }` (keys from {@link LEVEL_UP_LOCALES}) — `en` required and visibly non-blank; `nl` / `fr` non-blank when present. */
export function localizedTextSchema({ max, multiline = false }) {
    const text = surveyText(max, { min: 1, multiline });
    // The optional keys come from LEVEL_UP_LOCALES — the one list of locales.
    const translations = Object.fromEntries(LEVEL_UP_LOCALES.filter((locale) => locale !== "en").map((locale) => [locale, text.optional()]));
    return z
        .object({
        en: text,
        ...translations,
    })
        .strict();
}
/** The terms-and-conditions body a school accepts on enrollment (`level_up_terms.body`). */
export const levelUpTermsBodySchema = localizedTextSchema({ max: LEVEL_UP_TERMS_BODY_MAX, multiline: true });
export const levelUpKeySchema = z
    .string()
    .regex(LEVEL_UP_KEY_PATTERN, `Use ${LEVEL_UP_KEY_MIN}–${LEVEL_UP_KEY_MAX} lowercase letters, digits or hyphens`);
/** A link: an `http(s)` URL with an optional title. */
export const levelUpLinkSchema = z
    .object({
    title: surveyText(LEVEL_UP_LINK_TITLE_MAX, { min: 1 }).optional(),
    url: z
        .string()
        .trim()
        .max(LEVEL_UP_URL_MAX)
        .url()
        .regex(/^https?:\/\//i, "Use an http or https URL"),
})
    .strict();
const linksSchema = (max) => z.array(levelUpLinkSchema).max(max).default([]);
const frameworkValuesSchema = z
    .array(surveyText(LEVEL_UP_FRAMEWORK_VALUE_MAX, { min: 1 }))
    .max(LEVEL_UP_MAX_FRAMEWORK_VALUES)
    .default([]);
export const levelUpFrameworksSchema = z
    .object({
    greenRibbonPillars: frameworkValuesSchema,
    impactValueChain: frameworkValuesSchema,
})
    .strict();
/** The Green Ribbon pillar codes a tier maps to (`frameworks.greenRibbonPillars`; labels in i18n `greenRibbon.*`). */
export const LEVEL_UP_GREEN_RIBBON = ["ESL", "HP", "NI"];
/** The Impact Value Chain codes a tier maps to (`frameworks.impactValueChain`; labels in i18n `impactValueChain.*`). */
export const LEVEL_UP_IMPACT_VALUE_CHAIN = ["Inp", "Act", "Otp"];
/**
 * A pillar's colour: one of these lowercase hex values (labels in i18n
 * `colors.<hex>`). The wireframe's three, then the brand palette.
 */
export const LEVEL_UP_PILLAR_COLORS = [
    "#e0744f",
    "#15a267",
    "#c99f0c",
    "#1b6b6b",
    "#3a9d9d",
    "#d64b8a",
    "#5a3d7e",
    "#8cb82b",
];
/** A pillar's icon: a lucide icon name (labels in i18n `icons.*`). */
export const LEVEL_UP_PILLAR_ICONS = ["GraduationCap", "Users", "Home", "Leaf", "Droplet", "Zap", "Recycle", "Sun"];
/** Difficulty, impact and cost: whole numbers 0..{@link LEVEL_UP_MAX_RATING}. */
const ratingSchema = z.number().int().min(0).max(LEVEL_UP_MAX_RATING);
/** The tier names of `metadata.tierNames` are keyed "0".."9". */
export const LEVEL_UP_TIER_KEYS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
const tierNameSchema = localizedTextSchema({ max: LEVEL_UP_TIER_NAME_MAX });
// ---------------------------------------------------------------------------
// The package
// ---------------------------------------------------------------------------
export const levelUpTierSchema = z
    .object({
    level: z.number().int().min(0).max(LEVEL_UP_MAX_TIER_LEVEL),
    name: tierNameSchema,
    label: localizedTextSchema({ max: LEVEL_UP_TIER_LABEL_MAX }),
    statement: localizedTextSchema({ max: LEVEL_UP_STATEMENT_MAX, multiline: true }),
    /** A placeholder tier ("There are no requirements for this Tier metric"). */
    isEmpty: z.boolean().default(false),
    difficulty: ratingSchema,
    impact: ratingSchema,
    cost: ratingSchema,
    howToLinks: linksSchema(LEVEL_UP_MAX_LINKS),
    frameworks: levelUpFrameworksSchema.default({}),
    evidenceRequired: z.boolean().default(false),
})
    .strict();
export const levelUpMetricSchema = z
    .object({
    key: levelUpKeySchema,
    pillarKey: levelUpKeySchema,
    number: z.number().int().min(1).max(LEVEL_UP_MAX_METRIC_NUMBER),
    subcategory: localizedTextSchema({ max: LEVEL_UP_SUBCATEGORY_MAX }),
    focus: localizedTextSchema({ max: LEVEL_UP_FOCUS_MAX }),
    description: localizedTextSchema({ max: LEVEL_UP_DESCRIPTION_MAX, multiline: true }),
    links: linksSchema(LEVEL_UP_MAX_LINKS),
    tiers: z
        .array(levelUpTierSchema)
        .min(1)
        .max(LEVEL_UP_MAX_TIER_LEVEL + 1),
})
    .strict();
export const levelUpPillarSchema = z
    .object({
    key: levelUpKeySchema,
    name: localizedTextSchema({ max: LEVEL_UP_NAME_MAX }),
    color: z.enum(LEVEL_UP_PILLAR_COLORS).optional(),
    icon: z.enum(LEVEL_UP_PILLAR_ICONS).optional(),
})
    .strict();
export const levelUpBadgeBandsSchema = z
    .array(z.number().gt(0).lt(100))
    .length(LEVEL_UP_BADGE_BAND_COUNT)
    .refine((bands) => bands.every((b, i) => i === 0 || b > bands[i - 1]), "Badge bands must be ascending");
export const levelUpMetadataSchema = z
    .object({
    /** Default tier names; a tier's own `name` wins over these. */
    tierNames: z.record(z.enum(LEVEL_UP_TIER_KEYS), tierNameSchema),
    badgeBands: levelUpBadgeBandsSchema,
    helpLinks: linksSchema(LEVEL_UP_MAX_HELP_LINKS),
})
    .strict();
export const levelUpPackageSchema = z
    .object({
    format: z.literal(LEVEL_UP_PACKAGE_FORMAT),
    assessment: z
        .object({
        key: levelUpKeySchema,
        name: localizedTextSchema({ max: LEVEL_UP_NAME_MAX }),
    })
        .strict(),
    metadata: levelUpMetadataSchema,
    // Empty is valid (a new draft); publishing needs at least one of each
    // (lint blockers NO_PILLARS / NO_METRICS, re-checked by the publish RPC).
    pillars: z.array(levelUpPillarSchema).max(LEVEL_UP_MAX_PILLARS),
    metrics: z.array(levelUpMetricSchema).max(LEVEL_UP_MAX_METRICS),
})
    .strict()
    .superRefine((pkg, ctx) => {
    const duplicate = (values, index) => values.indexOf(values[index]) !== index;
    const pillarKeys = pkg.pillars.map((p) => p.key);
    pillarKeys.forEach((key, i) => {
        if (duplicate(pillarKeys, i)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["pillars", i, "key"], message: `Duplicate pillar key "${key}"` });
        }
    });
    const metricKeys = pkg.metrics.map((m) => m.key);
    const metricNumbers = pkg.metrics.map((m) => m.number);
    const usedLevels = new Set();
    pkg.metrics.forEach((metric, i) => {
        if (duplicate(metricKeys, i)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["metrics", i, "key"], message: `Duplicate metric key "${metric.key}"` });
        }
        if (duplicate(metricNumbers, i)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["metrics", i, "number"],
                message: `Duplicate metric number ${metric.number}`,
            });
        }
        if (!pillarKeys.includes(metric.pillarKey)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["metrics", i, "pillarKey"],
                message: `Unknown pillar "${metric.pillarKey}"`,
            });
        }
        // Tiers run contiguously 0..N in array order (no SQL backstop — Phase 2 publish re-checks).
        metric.tiers.forEach((tier, t) => {
            usedLevels.add(tier.level);
            if (tier.level !== t) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["metrics", i, "tiers", t, "level"],
                    message: `Tiers must run 0..N in order: expected tier ${t}, got ${tier.level}`,
                });
            }
            if (tier.level === 0 && (tier.difficulty !== 0 || tier.impact !== 0)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["metrics", i, "tiers", t],
                    message: "Tier 0 has no difficulty or impact",
                });
            }
        });
    });
    for (const level of usedLevels) {
        const key = String(level);
        if (!pkg.metadata.tierNames[key]) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["metadata", "tierNames", key],
                message: `Tier ${level} has no name`,
            });
        }
    }
});
//# sourceMappingURL=package.js.map